import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, MessageCircle, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError('Failed to send message. Please try again or contact us via WhatsApp.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Chat with us directly',
      action: 'Start Chat',
      href: 'https://wa.me/61415145952?text=Hi%2C%20I%20have%20a%20question',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'charmingcreationsgifts@gmail.com',
      action: 'Send Email',
      href: 'mailto:charmingcreationsgifts@gmail.com',
      color: 'bg-amber-500 hover:bg-amber-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-teal-50">
      <Helmet>
        <title>Contact Us | Charming Creations Australia</title>
        <meta name="description" content="Get in touch with Charming Creations. Contact us via WhatsApp, email, or our contact form. We're here to help with orders, custom requests, and questions." />
        <link rel="canonical" href="https://www.charmingcreationsgifts.com/contact" />
      </Helmet>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-12 h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-teal-600 bg-clip-text text-transparent">
                  Charming Creations
                </h1>
              </div>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <Mail className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Have a question, custom order request, or just want to say hello? We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Methods */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Contact</h2>
              
              {contactMethods.map((method, idx) => (
                <Card key={idx} className="border-2 border-gray-100 hover:border-amber-200 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${method.color.split(' ')[0]} text-white`}>
                        <method.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-1">{method.title}</h3>
                        <p className="text-gray-600 text-sm mb-3">{method.description}</p>
                        <a href={method.href} target="_blank" rel="noopener noreferrer">
                          <Button className={`${method.color} text-white`}>
                            {method.action}
                          </Button>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Info Cards */}
              <Card className="border-2 border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Response Time</h3>
                      <p className="text-gray-600 text-sm">
                        We typically respond within 24 hours. For urgent enquiries, WhatsApp is the fastest way to reach us!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-teal-200 bg-teal-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-teal-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Location</h3>
                      <p className="text-gray-600 text-sm">
                        We're based in Australia and ship nationwide with FREE delivery on all orders!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send a Message</h2>
              
              {submitted ? (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Send Another Message
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-2 border-gray-100">
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                          <Input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            placeholder="Your name"
                            className="border-2 border-gray-200 focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            required
                            placeholder="your@email.com"
                            className="border-2 border-gray-200 focus:border-amber-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                        <Input
                          type="text"
                          value={form.subject}
                          onChange={(e) => setForm({ ...form, subject: e.target.value })}
                          required
                          placeholder="What's this about?"
                          className="border-2 border-gray-200 focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                        <Textarea
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          required
                          placeholder="Tell us how we can help..."
                          rows={5}
                          className="border-2 border-gray-200 focus:border-amber-500"
                        />
                      </div>
                      
                      {error && (
                        <p className="text-red-500 text-sm">{error}</p>
                      )}
                      
                      <Button 
                        type="submit" 
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-amber-500 to-teal-500 hover:from-amber-600 hover:to-teal-600 text-white py-6"
                      >
                        {submitting ? (
                          <span className="flex items-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <Send className="w-5 h-5 mr-2" />
                            Send Message
                          </span>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
